import React, { useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { App, DatePicker, Form, InputNumber, Modal, Select } from "antd";

import { apiLogOptionTrade } from "api/apiOptionStrategies";
import { OPTION_MULTIPLIER, formatMoney } from "./optionTradeUtils";
import { marketToday } from "./optionStrategyUtils";

// Log an option position bought for an option strategy. Prefilled from the live
// ATM call snapshot when there is one.
const OpenOptionTradeModal = (props) => {
  const { item, snapshot, onClose, onSaved } = props;
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isSaving, setIsSaving] = useState(false);
  const premium = Form.useWatch("premium", form);
  const contracts = Form.useWatch("contracts", form);
  const userPremiumFee = useSelector(
    (state) => state.main.user.preferences?.premiumFee,
  );

  const cost = (premium || 0) * (contracts || 0) * OPTION_MULTIPLIER;

  const onFinish = async (values) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await apiLogOptionTrade({
        strategyId: item._id,
        optionType: values.optionType,
        strikePrice: values.strikePrice,
        expirationDate: values.expirationDate.format("YYYY-MM-DD"),
        contracts: values.contracts,
        premium: values.premium,
        fees: values.fees,
        openDate: values.openDate.format("YYYY-MM-DD"),
        underlyingPrice: values.underlyingPrice,
      });
      message.success(`${item.symbol} option trade logged`);
      onSaved();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to log trade");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={`Log option trade - ${item.symbol}`}
      open
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={isSaving}
      okText="Log trade"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          optionType: "call",
          strikePrice: snapshot?.strike,
          expirationDate: snapshot?.expiration
            ? dayjs(snapshot.expiration, "YYYYMMDD")
            : undefined,
          contracts: 1,
          premium: snapshot?.entryPrice || undefined,
          fees: userPremiumFee ?? 0,
          openDate: marketToday(),
          underlyingPrice: snapshot?.underlyingPrice || undefined,
        }}
      >
        <Form.Item name="optionType" label="Type">
          <Select
            options={[
              { label: "Call", value: "call" },
              { label: "Put", value: "put" },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="strikePrice"
          label="Strike"
          rules={[{ required: true, message: "Strike is required" }]}
        >
          <InputNumber style={{ width: "100%" }} min={0} step={0.5} />
        </Form.Item>
        <Form.Item
          name="expirationDate"
          label="Expiration"
          rules={[{ required: true, message: "Expiration is required" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="contracts"
          label="Contracts"
          rules={[{ required: true, message: "Contracts are required" }]}
        >
          <InputNumber style={{ width: "100%" }} min={1} precision={0} />
        </Form.Item>
        <Form.Item
          name="premium"
          label="Premium per share"
          extra={`Cost: $${formatMoney(cost)} (${OPTION_MULTIPLIER} shares per contract)`}
          rules={[{ required: true, message: "Premium is required" }]}
        >
          <InputNumber style={{ width: "100%" }} min={0.01} step={0.01} />
        </Form.Item>
        <Form.Item name="fees" label="Commission">
          <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
        </Form.Item>
        <Form.Item
          name="openDate"
          label="Open date"
          rules={[{ required: true, message: "Open date is required" }]}
        >
          <DatePicker style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item name="underlyingPrice" label="Underlying price (optional)">
          <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

OpenOptionTradeModal.propTypes = {
  item: PropTypes.object.isRequired,
  snapshot: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default OpenOptionTradeModal;
